import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { MessageCircle, Mail, Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const ShareModal = ({ open, onClose }) => {
  const [copied, setCopied] = useState(false);
  const url = 'https://2tap.app';
  const shareText = 'Check out 2Tap - Your Office in Your Pocket!';
  const fullMessage = `${shareText} ${url}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for PWA - select text approach
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success('Link copied!');
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        toast.error('Could not copy. Please copy manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleSMS = () => {
    const message = encodeURIComponent(fullMessage);
    window.location.href = `sms:?body=${message}`;
    onClose();
  };

  const handleEmail = () => {
    const subject = encodeURIComponent('Check out 2Tap!');
    const body = encodeURIComponent(`${shareText}\n\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    onClose();
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(fullMessage);
    window.open(`https://wa.me/?text=${message}`, '_blank');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[320px] rounded-2xl p-0 overflow-hidden">
        <DialogHeader className="bg-[#1F2A37] text-white p-4 pb-3">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-bold">Share 2Tap</DialogTitle>
            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-slate-300 mt-1">Invite a mate to try 2Tap</p>
        </DialogHeader>
        
        <div className="p-4 space-y-3">
          {/* Share Options */}
          <button
            onClick={handleSMS}
            className="w-full flex items-center gap-4 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors text-left"
          >
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">Message</p>
              <p className="text-sm text-slate-500">Send via SMS or iMessage</p>
            </div>
          </button>

          <button
            onClick={handleEmail}
            className="w-full flex items-center gap-4 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors text-left"
          >
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">Email</p>
              <p className="text-sm text-slate-500">Send an email invite</p>
            </div>
          </button>

          <button
            onClick={handleWhatsApp}
            className="w-full flex items-center gap-4 p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors text-left"
          >
            <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div>
              <p className="font-semibold text-slate-800">WhatsApp</p>
              <p className="text-sm text-slate-500">Share on WhatsApp</p>
            </div>
          </button>

          <button
            onClick={handleCopy}
            className="w-full flex items-center gap-4 p-4 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors text-left"
          >
            <div className={`w-12 h-12 ${copied ? 'bg-green-500' : 'bg-slate-500'} rounded-full flex items-center justify-center transition-colors`}>
              {copied ? <Check className="w-6 h-6 text-white" /> : <Copy className="w-6 h-6 text-white" />}
            </div>
            <div>
              <p className="font-semibold text-slate-800">{copied ? 'Copied!' : 'Copy Link'}</p>
              <p className="text-sm text-slate-500 truncate max-w-[180px]">{url}</p>
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
